import React, { useState } from 'react'
import {
  Button,
  Caption1,
  Field,
  Textarea,
  Dropdown,
  Option,
  Spinner,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import {
  SparkleRegular,
  TextGrammarWandRegular,
  TextboxAlignMiddleRegular,
  DocumentTextRegular,
  ArrowDownloadRegular,
  DismissRegular,
} from '@fluentui/react-icons'
import { AIGenerationRequest, AITone, AIStyle } from '@templatean/shared/types'
import { aiService } from '@templatean/shared/services'
import { useOffice } from '@templatean/shared/hooks'
import { useNotify } from '../hooks/useNotify'
import { SectionHeader, ErrorState } from './ui'

const TONES: { value: AITone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'formal', label: 'Formal' },
  { value: 'enthusiastic', label: 'Enthusiastic' },
]

const STYLES: { value: AIStyle; label: string }[] = [
  { value: 'concise', label: 'Concise' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'technical', label: 'Technical' },
  { value: 'creative', label: 'Creative' },
  { value: 'informative', label: 'Informative' },
]

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalL },
  stack: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM },
  full: { width: '100%', minWidth: 0 },
  textarea: { width: '100%', minHeight: '96px' },
  // Two option fields side by side, stacking when the pane is narrow.
  options: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  preview: {
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground1,
    whiteSpace: 'pre-wrap',
    maxHeight: '240px',
    overflowY: 'auto',
  },
  row: { display: 'flex', columnGap: tokens.spacingHorizontalS },
  grow: { flexGrow: 1 },
  quick: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
})

const AIAssistant: React.FC = () => {
  const styles = useStyles()
  const notify = useNotify()
  const { insertText, getSelection } = useOffice()
  const [prompt, setPrompt] = useState('')
  const [tone, setTone] = useState<AITone>('professional')
  const [style, setStyle] = useState<AIStyle>('concise')
  const [loading, setLoading] = useState(false)
  const [working, setWorking] = useState<string | null>(null)
  const [generatedContent, setGeneratedContent] = useState('')
  const [error, setError] = useState<string | null>(null)

  const promptMissing = prompt.trim().length === 0

  const handleGenerate = async () => {
    if (promptMissing) return

    setLoading(true)
    setError(null)
    try {
      const selection = await getSelection()
      const request: AIGenerationRequest = {
        prompt,
        context: selection?.text,
        options: { tone, style, format: 'text', maxTokens: 1000 },
      }

      const response = await aiService.generateContent(request)
      setGeneratedContent(response.content)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The content service did not respond.')
    } finally {
      setLoading(false)
    }
  }

  const handleInsert = async () => {
    if (!generatedContent) return

    try {
      await insertText(generatedContent)
      notify({ title: 'Content inserted' })
      setGeneratedContent('')
      setPrompt('')
    } catch (err) {
      notify({
        intent: 'error',
        title: 'Could not insert the content',
        body: err instanceof Error ? err.message : 'Word refused the request.',
      })
    }
  }

  /** Shared runner for the three selection-based tools. */
  const runOnSelection = async (
    id: string,
    title: string,
    work: (text: string) => Promise<string | void>
  ) => {
    if (working) return

    setError(null)
    try {
      const selection = await getSelection()
      if (!selection?.text?.trim()) {
        notify({
          intent: 'warning',
          title: 'Select some text first',
          body: 'Highlight the text in your document you want to work on.',
        })
        return
      }

      setWorking(id)
      const detail = await work(selection.text)
      notify({ title, body: typeof detail === 'string' ? detail : undefined })
    } catch (err) {
      notify({
        intent: 'error',
        title: `Could not ${title.toLowerCase()}`,
        body: err instanceof Error ? err.message : 'The content service did not respond.',
      })
    } finally {
      setWorking(null)
    }
  }

  const handleEnhance = () =>
    runOnSelection('enhance', 'Selection enhanced', async text => {
      const enhancements = await aiService.enhanceContent(text, ['grammar', 'clarity', 'style'])
      const suggestion = enhancements[0]?.suggestions?.[0]?.suggested
      if (!suggestion) throw new Error('No improvement was suggested for that selection.')
      await insertText(suggestion)
    })

  const handleSummarise = () =>
    runOnSelection('summarise', 'Summary ready', async text => {
      const response = await aiService.generateContent({
        prompt: `Summarise the following text.\n\n${text}`,
        options: { tone, style: 'concise', format: 'text', maxTokens: 400 },
      })
      setGeneratedContent(response.content)
      return 'Review it below, then insert.'
    })

  const handleGrammar = () =>
    runOnSelection('grammar', 'Grammar checked', async text => {
      const enhancements = await aiService.enhanceContent(text, ['grammar'])
      const issues = enhancements[0]?.suggestions?.length ?? 0
      return issues === 0 ? 'No issues found.' : `${issues} suggestion${issues === 1 ? '' : 's'} found.`
    })

  return (
    <div className={styles.root}>
      <div className={styles.stack}>
        <SectionHeader>Generate content</SectionHeader>

        {error && <ErrorState title="Generation failed" detail={error} onRetry={handleGenerate} />}

        <Field
          label="What should it write?"
          required
          hint="Anything you have selected in the document is used as context."
        >
          <Textarea
            className={styles.textarea}
            placeholder="A short covering note for a proposal to a new client"
            value={prompt}
            onChange={(_, data) => setPrompt(data.value)}
            resize="vertical"
          />
        </Field>

        <div className={styles.options}>
          <Field label="Tone">
            <Dropdown
              className={styles.full}
              selectedOptions={[tone]}
              value={TONES.find(t => t.value === tone)?.label ?? ''}
              onOptionSelect={(_, data) => setTone((data.optionValue as AITone) ?? 'professional')}
            >
              {TONES.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Style">
            <Dropdown
              className={styles.full}
              selectedOptions={[style]}
              value={STYLES.find(s => s.value === style)?.label ?? ''}
              onOptionSelect={(_, data) => setStyle((data.optionValue as AIStyle) ?? 'concise')}
            >
              {STYLES.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        <Button
          appearance="primary"
          icon={loading ? <Spinner size="tiny" /> : <SparkleRegular />}
          disabled={promptMissing || loading}
          onClick={handleGenerate}
        >
          {loading ? 'Generating' : 'Generate content'}
        </Button>
      </div>

      {generatedContent && (
        <div className={styles.stack}>
          <SectionHeader>Result</SectionHeader>
          <div className={styles.preview}>
            <Text size={200}>{generatedContent}</Text>
          </div>
          <div className={styles.row}>
            <Button
              className={styles.grow}
              appearance="primary"
              icon={<ArrowDownloadRegular />}
              onClick={handleInsert}
            >
              Insert into document
            </Button>
            <Button
              appearance="secondary"
              icon={<DismissRegular />}
              onClick={() => setGeneratedContent('')}
            >
              Discard
            </Button>
          </div>
        </div>
      )}

      <div className={styles.stack}>
        <SectionHeader>Work on the selection</SectionHeader>
        <Caption1>Select text in your document, then choose a tool.</Caption1>

        <div className={styles.quick}>
          <Button
            icon={working === 'enhance' ? <Spinner size="tiny" /> : <TextboxAlignMiddleRegular />}
            disabled={working !== null}
            onClick={handleEnhance}
          >
            Enhance selected text
          </Button>
          <Button
            icon={working === 'summarise' ? <Spinner size="tiny" /> : <DocumentTextRegular />}
            disabled={working !== null}
            onClick={handleSummarise}
          >
            Summarise selection
          </Button>
          <Button
            icon={working === 'grammar' ? <Spinner size="tiny" /> : <TextGrammarWandRegular />}
            disabled={working !== null}
            onClick={handleGrammar}
          >
            Check grammar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AIAssistant
